import { PayrollSettings, PayrollFrequency, NameDisplayFormat, Schedules, Shift, DisplayGroup, DayOfWeek, RateDefinition } from './types.js';

export function cloneDeep<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
}

export function formatEmployeeName(employee: { firstName: string, lastName: string }, format?: NameDisplayFormat): string {
    const { firstName, lastName } = employee;
    const nameFormat = format || NameDisplayFormat.FIRST_LAST;

    switch (nameFormat) {
        case NameDisplayFormat.LAST_FIRST:
            return `${lastName || ''}, ${firstName || ''}`.trim();
        case NameDisplayFormat.FIRST_LAST:
        default:
            return `${firstName || ''} ${lastName || ''}`.trim();
    }
}

export const correctDateForTimezone = (dateStr: string): Date => {
    const date = new Date(dateStr);
    const employeeTimezoneOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() + employeeTimezoneOffset);
};

export function calculatePeriodDates(settings: PayrollSettings): { start: Date, end: Date } {
    const start = correctDateForTimezone(settings.startDate);
    let end = new Date(start);

    switch (settings.frequency) {
        case PayrollFrequency.WEEKLY:
            end.setDate(start.getDate() + 6);
            break;
        case PayrollFrequency.BIWEEKLY:
            end.setDate(start.getDate() + 13);
            break;
        case PayrollFrequency.SEMI_MONTHLY:
            if (start.getDate() === 1) {
                end.setDate(15);
            } else {
                end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
            }
            break;
        case PayrollFrequency.MONTHLY:
            end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
            break;
        case PayrollFrequency.EMPLOYEE_DEFINED:
            if (settings.endDate) {
                end = correctDateForTimezone(settings.endDate);
            }
            break;
    }

    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
}

export function calculatePreviousPeriodStartDate(settings: PayrollSettings): Date {
    const { startDate: currentStartDateString } = settings;
    const currentStart = correctDateForTimezone(currentStartDateString);

    let lastPeriodStartDate: Date;

    switch (settings.frequency) {
        case PayrollFrequency.WEEKLY:
            lastPeriodStartDate = new Date(currentStart);
            lastPeriodStartDate.setDate(currentStart.getDate() - 7);
            break;
        case PayrollFrequency.BIWEEKLY:
            lastPeriodStartDate = new Date(currentStart);
            lastPeriodStartDate.setDate(currentStart.getDate() - 14);
            break;
        case PayrollFrequency.SEMI_MONTHLY:
            if (currentStart.getDate() > 15) {
                lastPeriodStartDate = new Date(currentStart.getFullYear(), currentStart.getMonth(), 1);
            } else {
                lastPeriodStartDate = new Date(currentStart.getFullYear(), currentStart.getMonth() - 1, 16);
            }
            break;
        case PayrollFrequency.MONTHLY:
            lastPeriodStartDate = new Date(currentStart.getFullYear(), currentStart.getMonth() - 1, 1);
            break;
        case PayrollFrequency.EMPLOYEE_DEFINED:
            if (settings.endDate) {
                const currentStartDt = correctDateForTimezone(settings.startDate);
                const currentEndDt = correctDateForTimezone(settings.endDate);
                const durationMs = currentEndDt.getTime() - currentStartDt.getTime();
                
                const lastPeriodEnd = new Date(currentStartDt);
                lastPeriodEnd.setDate(currentStartDt.getDate() - 1);
                
                const lastPeriodStart = new Date(lastPeriodEnd.getTime() - durationMs);
                return lastPeriodStart;
            }
            return currentStart;
        default:
            return currentStart;
    }
    return lastPeriodStartDate;
}

export const toDateInputString = (d: Date): string => {
    const date = new Date(d);
    if (isNaN(date.getTime())) {
        return '';
    }
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date.toISOString().split('T')[0];
};

export const toTimeInputString = (d: Date): string => {
    const date = new Date(d);
    if (isNaN(date.getTime())) {
        return '';
    }
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date.toISOString().split('T')[1].slice(0, 5);
};

export function calculateEndDate(startDate: Date, days: number, skipSaturday: boolean, skipSunday: boolean): Date {
    if (days <= 0) {
        return new Date(startDate.getTime());
    }

    let currentDate = new Date(startDate.getTime());
    let daysRemaining = days;

    while (daysRemaining > 0) {
        const dayOfWeek = currentDate.getDay();
        const isSkipped = (skipSaturday && dayOfWeek === 6) || (skipSunday && dayOfWeek === 0);

        if (!isSkipped) {
            daysRemaining--;
        }
        if (daysRemaining > 0) {
            currentDate.setDate(currentDate.getDate() + 1);
        }
    }

    return currentDate;
}

export function calculateDaysBetween(start: Date, end: Date, skipSaturday: boolean, skipSunday: boolean): number {
    if (end < start) return 0;
    let count = 0;
    const current = new Date(start);
    while (current.getTime() <= end.getTime()) {
        const dayOfWeek = current.getDay();
        if (!((skipSaturday && dayOfWeek === 6) || (skipSunday && dayOfWeek === 0))) {
            count++;
        }
        current.setDate(current.getDate() + 1);
    }
    return count;
}


export function getTodaysShiftForEmployee(
    employeeId: string,
    schedules: Schedules,
    shifts: Shift[],
    payrollSettings: PayrollSettings,
    displayGroups: DisplayGroup[],
    referenceDate: Date = new Date(),
    checkGroupFallback: boolean = true
): Shift | null {
    if (payrollSettings.startDate) {
        const cycleLength = payrollSettings.frequency === PayrollFrequency.BIWEEKLY ? 14 : payrollSettings.frequency === PayrollFrequency.WEEKLY ? 7 : 0;
        if (cycleLength > 0) {
            const employeeSchedule = schedules[employeeId];
            if (employeeSchedule && employeeSchedule.length > 0) {
                const start = correctDateForTimezone(payrollSettings.startDate);
                const msInDay = 24 * 60 * 60 * 1000;
                const todayMidnight = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate()).getTime();
                const startMidnight = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
                const daysPassed = Math.round((todayMidnight - startMidnight) / msInDay);

                if (daysPassed >= 0) {
                    const dayIndex = daysPassed % cycleLength;
                    const shiftId = employeeSchedule[dayIndex];
                    if (shiftId) {
                        const foundShift = shifts.find(s => s.id === shiftId);
                        if (foundShift) return foundShift;
                    }
                }
            }
        }
    }
    
    if (!checkGroupFallback) {
        return null;
    }

    const getGroupFor = (empId: string, dGroups: DisplayGroup[]) => {
        const employeeGroups = dGroups.filter(g => g.employeeIds.includes(empId));
        if (employeeGroups.length === 0) return undefined;
        const specificGroup = employeeGroups.find(g => !g.isDefault);
        return specificGroup || employeeGroups[0];
    };
    
    const group = getGroupFor(employeeId, displayGroups);
    if (group?.settings.shiftId) {
        const foundShift = shifts.find(s => s.id === group.settings.shiftId);
        if (foundShift) return foundShift;
    }

    return null;
}

export const createDefaultDailyConditions = (): Record<DayOfWeek, { enabled: boolean; threshold: number }> => {
    return Object.values(DayOfWeek).reduce((acc, day) => {
        acc[day] = { enabled: false, threshold: 8 };
        return acc;
    }, {} as Record<DayOfWeek, { enabled: boolean, threshold: number }>);
};

export const createEmptyRateDefinition = (): RateDefinition => {
    return {
        rate: 1.0,
        weekly: { enabled: false, threshold: 40 },
        daily: createDefaultDailyConditions(),
    };
};